/**
 * Audit Filters Hook
 *
 * Parses the generic Filter component's activeFilters state
 * into local filter/sort functions applied on the frontend.
 */

import { useMemo } from "react";
import { useFilter } from "@/components/ui";
import type { UIAudit } from "@/packages/types";

interface AuditFiltersInput {
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface AuditFiltersResult {
  filterAudits: (audits: UIAudit[]) => UIAudit[];
}

export function useAuditFilters(input?: AuditFiltersInput): AuditFiltersResult {
  const { filters } = useFilter();

  const filterAudits = useMemo(() => {
    return (audits: UIAudit[]): UIAudit[] => {
      let result = audits;

      // Apply category filters
      for (const filter of filters) {
        if (filter.valueIds.length === 0) continue;

        const values = new Set(filter.valueIds);

        switch (filter.categoryId) {
          case "status": {
            switch (filter.operator) {
              case "is":
              case "is_either_of":
                result = result.filter((a) => values.has(a.status));
                break;
              case "is_not":
                result = result.filter((a) => !values.has(a.status));
                break;
            }
            break;
          }
          case "trigger": {
            switch (filter.operator) {
              case "is":
              case "is_either_of":
                result = result.filter(
                  (a) => a.trigger && values.has(a.trigger),
                );
                break;
              case "is_not":
                result = result.filter(
                  (a) => !a.trigger || !values.has(a.trigger),
                );
                break;
            }
            break;
          }
          case "tags": {
            switch (filter.operator) {
              case "includes_any":
                result = result.filter((a) =>
                  a.tags?.some((t) => values.has(t.id)),
                );
                break;
              case "includes_all":
                result = result.filter((a) =>
                  filter.valueIds.every((id) =>
                    a.tags?.some((t) => t.id === id),
                  ),
                );
                break;
              case "includes_none":
                result = result.filter(
                  (a) => !a.tags?.some((t) => values.has(t.id)),
                );
                break;
            }
            break;
          }
        }
      }

      // Apply search
      if (input?.search) {
        const query = input.search.toLowerCase();
        result = result.filter(
          (a) =>
            a.displayName.toLowerCase().includes(query) ||
            a.code.toLowerCase().includes(query) ||
            a.description?.toLowerCase().includes(query),
        );
      }

      // Apply sort
      if (input?.sortBy) {
        const dir = input.sortOrder === "desc" ? -1 : 1;
        result = [...result].sort((a, b) => {
          switch (input.sortBy) {
            case "deadline":
              return (a.deadline - b.deadline) * dir;
            case "displayName":
              return a.displayName.localeCompare(b.displayName) * dir;
            case "status":
              return a.status.localeCompare(b.status) * dir;
            case "createdAt":
              return (a.createdAt - b.createdAt) * dir;
            default:
              return 0;
          }
        });
      }

      return result;
    };
  }, [filters, input?.search, input?.sortBy, input?.sortOrder]);

  return { filterAudits };
}
